import React, { useState, useEffect } from 'react';
import { makeSelectForm } from '../../../store/form/form.selectors';
import { EInput } from '../../../domain/input.interface';
import { useDispatch, useSelector } from 'react-redux';
import { Field, reduxForm, reset, initialize } from 'redux-form';
import { InputFieldComponent } from '../../atoms/atoms.index';
import { required } from '../../../utils/FormValidators';
import { FormattedMessage } from 'react-intl';
import {
  ImageUploaderComponent,
  NavbarComponent,
  NavigationMenuComponent,
} from '../../organisms/organisms.index';
import { returnNotification } from '../../../store/render-controller/render-controller.actions';
import { ENotification } from '../../../domain/notification.interface';
import { TextareaFieldComponent } from '../../atoms/textareaField/TextareaField.component';
import {
  loadWish,
  resetWish,
  editWish,
  newWish,
} from '../../../store/wishlist/wishlist.actions';
import { makeSelectWish } from '../../../store/wishlist/wishlist.selectors';
import moment from 'moment';
import _ from 'lodash';
import { EFormName } from '../../../config/form-names.config';
import {
  normalizeCurrency,
  formatNumberToCurrency,
  formatCurrencyToNumber,
} from '../../../utils/FormNormalizers';
import './NewWish.component.scss';

function NewWishFormComponent(props: any) {
  const [trigger, setTrigger] = useState(false);
  const [isNewWish, setIsNewWish] = useState(false);
  const [images, setImages] = useState<any>({});
  const [imagesToRemove, setImagesToRemove] = useState<string[] | undefined>(
    [],
  );
  const dispatch = useDispatch();
  const form = useSelector(makeSelectForm());
  const wish = useSelector(makeSelectWish());
  const updatedAt = moment(wish.updatedAt).format('DD-MM-YYYY');

  useEffect(() => {
    if (_.includes(props.match.url, 'newwish')) {
      setIsNewWish(true);
      dispatch(resetWish());
    } else {
      dispatch(loadWish(props.match.params.wishId));
    }
    return () => {
      dispatch(resetWish());
      dispatch(reset(EFormName.NEW_WISH));
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (_.includes(props.match.url, 'newwish')) {
      return;
    }
    const formattedWish = {
      ...wish,
      price: formatNumberToCurrency(wish.price),
    };
    dispatch(initialize(EFormName.NEW_WISH, formattedWish));
    // eslint-disable-next-line
  }, [wish]);

  function handleResetForm(e: any) {
    e.preventDefault();
    dispatch(
      returnNotification({
        type: ENotification.WARNING,
        msg1: 'warning.confirmation.message',
        dispatch: { func: reset, param: [EFormName.NEW_WISH] },
        cb: { func: props.history.goBack },
      }),
    );
  }

  const checkErrors = () => {
    return form[EFormName.NEW_WISH].syncErrors ? true : false;
  };

  function handleNewWishSubmit(values: any) {
    if (checkErrors()) {
      setTrigger(true);
      return;
    }
    const formattedValues = !!values.price
      ? { ...values, price: formatCurrencyToNumber(values.price) }
      : { ...values };
    if (isNewWish) {
      dispatch(newWish(formattedValues, images, props.match.params.wishListId));
    } else {
      const { description, name, notes, price, store, url } = formattedValues;
      dispatch(
        editWish(
          { description, name, notes, price, store, url },
          images,
          imagesToRemove,
          wish.id,
          props.match.params.wishListId,
        ),
      );
    }
  }

  function handleNewImages(body: any, name: string) {
    const newImage = images;
    newImage[name] = body;
    setImages(newImage);
  }

  function handleOldImages(ids: string[]) {
    setImagesToRemove(ids);
  }

  return (
    <div className='new-wish-container page-container'>
      <div>
        <NavbarComponent />
        <NavigationMenuComponent {...props} />
      </div>
      <h2 className='new-wish-title'>
        {isNewWish ? (
          <FormattedMessage id='pages.new_wish_form.title_create' />
        ) : (
          <FormattedMessage id='pages.new_wish_form.title_modify' />
        )}
      </h2>
      <div className='new-wish-form-container'>
        <form
          onSubmit={props.handleSubmit((values: any) =>
            handleNewWishSubmit(values),
          )}
        >
          <div>
            <h4 className='subtitle'>
              <FormattedMessage id='pages.new_wish_form.name.title' />
            </h4>
            <FormattedMessage id='pages.new_wish_form.name.placeholder'>
              {(txt: any) => (
                <Field
                  name={'name'}
                  component={InputFieldComponent}
                  validate={[required]}
                  label={txt}
                  errorTrigger={trigger}
                />
              )}
            </FormattedMessage>
          </div>
          <div>
            <h4 className='subtitle'>
              <FormattedMessage id='pages.new_wish_form.main_photo.title' />
            </h4>
            <div>
              <ImageUploaderComponent
                name='main_photo'
                handleNewImages={handleNewImages}
                maxNumImg={1}
                propsImages={wish.mainPhoto}
              />
            </div>
          </div>
          <div>
            <h4 className='subtitle'>
              <FormattedMessage id='pages.new_wish_form.description.title' />
            </h4>
            <FormattedMessage id='pages.new_wish_form.description.placeholder'>
              {(txt: any) => (
                <Field
                  name={'description'}
                  component={TextareaFieldComponent}
                  validate={[]}
                  label={txt}
                  errorTrigger={trigger}
                />
              )}
            </FormattedMessage>
          </div>
          <div>
            <h4 className='subtitle'>
              <FormattedMessage id='pages.new_wish_form.url.title' />
            </h4>
            <div>
              <FormattedMessage id='pages.new_wish_form.url.placeholder'>
                {(txt: any) => (
                  <Field
                    name={'url'}
                    component={InputFieldComponent}
                    validations={[]}
                    label={txt}
                    errorTrigger={trigger}
                  />
                )}
              </FormattedMessage>
            </div>
          </div>
          <div>
            <h4 className='subtitle'>
              <FormattedMessage id='pages.new_wish_form.optional_photos.title' />
            </h4>
            <div>
              <ImageUploaderComponent
                name='optional_photos'
                handleNewImages={handleNewImages}
                handleOldImages={handleOldImages}
                maxNumImg={5}
                propsImages={wish.optionalPhotos}
              />
            </div>
          </div>
          <div>
            <h4 className='subtitle'>
              <FormattedMessage id='pages.new_wish_form.price.title' />
            </h4>
            <FormattedMessage id='pages.new_wish_form.price.placeholder'>
              {(txt: any) => (
                <Field
                  name={'price'}
                  component={InputFieldComponent}
                  label={txt}
                  type={EInput.TEXT}
                  errorTrigger={trigger}
                  normalize={(value: number | string) =>
                    normalizeCurrency(value)
                  }
                />
              )}
            </FormattedMessage>
          </div>
          <div>
            <h4 className='subtitle'>
              <FormattedMessage id='pages.new_wish_form.store.title' />
            </h4>
            <FormattedMessage id='pages.new_wish_form.store.placeholder'>
              {(txt: any) => (
                <Field
                  name={'store'}
                  component={InputFieldComponent}
                  validate={[]}
                  label={txt}
                  errorTrigger={trigger}
                />
              )}
            </FormattedMessage>
          </div>
          <div>
            <h4 className='subtitle'>
              <FormattedMessage id='pages.new_wish_form.notes.title' />
            </h4>
            <FormattedMessage id='pages.new_wish_form.notes.placeholder'>
              {(txt: any) => (
                <Field
                  name={'notes'}
                  component={TextareaFieldComponent}
                  label={txt}
                  errorTrigger={trigger}
                />
              )}
            </FormattedMessage>
          </div>
          {!isNewWish && (
            <div className='new-wish-uneditables'>
              <p className='text-primary-lg font-weight-light'>
                <FormattedMessage id='pages.new_wish_form.status' />
                <FormattedMessage id={`common.wish.status_${wish.status}`} />
              </p>
              <p className='text-primary-lg font-weight-light'>
                <FormattedMessage id='pages.new_wish_form.last_modified' />
                {updatedAt}
              </p>
            </div>
          )}
          <section className='form-button-container mt-4'>
            <div className='left-wfc'>
              <button
                className='btn-lili danger'
                onClick={(e: any) => handleResetForm(e)}
              >
                <FormattedMessage id='common.cancel' />
              </button>
            </div>
            <div className='right-wfc'>
              <button className='btn-lili' type='submit'>
                <FormattedMessage id='pages.new_wish_form.save' />
              </button>
            </div>
          </section>
        </form>
      </div>
    </div>
  );
}

export default reduxForm({
  form: EFormName.NEW_WISH,
})(NewWishFormComponent);
